import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getSalesOrder, getSalesOrderItem, getSalesOrderStatuses, type SalesOrder, saveSalesShipment, SalesShipmentItem, type SalesShipment, getSalesShipment, getSalesShipmentItem } from "../../api/erp";
import { MDBBtn, MDBCard, MDBCardBody, MDBInput, MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import useLocale, { I18N } from "../../util/i18n";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { type OptionType } from "../../component/select";
import Toast from "../../component/toast";
import { useCrumbs } from "../../component/breadcrumbs";
import Autocomplete from "../../component/autocomplete";

const DEFAULT_STATUSES = [
    { value: 'Placed', label: 'Placed' },
    { value: 'Complete', label: 'Complete' },
    { value: 'Deleted', label: 'Deleted' }
];

export default function SalesShipment(props: { editable: boolean }) {
    let { 0: query } = useSearchParams();
    let { 0: shipment, 1: setShipment } = React.useState<SalesShipment>({ id: '', order_id: '', shipment_id: '', customer_id: '', customer_name: '', status: 'Placed' });
    let { 0: items, 1: setItems } = React.useState<Array<SalesShipmentItem>>([]);
    let { 0: statuses, 1: setStatuses } = React.useState<Array<OptionType>>(DEFAULT_STATUSES);
    let { 0: __, 1: lang } = useLocale();
    let { 0: setCrumbs } = useCrumbs();
    let navigate = useNavigate();
    let save = React.useCallback((e: React.FormEvent<HTMLFormElement>) => {
        e.stopPropagation();
        e.preventDefault();
        if (!(e.target as HTMLFormElement).checkValidity()) {
            return;
        }
        saveSalesShipment({ ...shipment, item: items }).then(response => {
            if (typeof response !== 'string' && response.data) {
                let statuses: Array<OptionType> = JSON.parse(window.sessionStorage.getItem('SS-status') ?? '[]');
                if (statuses.filter(p => p.value === shipment.status).length === 0) {
                    statuses.push({ value: shipment.status, label: shipment.status });
                    setStatuses(statuses);
                }
                navigate('/sales-shipment/edit?id=' + response.data, { replace: true });
            }
            Toast.show('Data has been saved.');
        }, () => Toast.show(__('An error detected. Please try again later.')));
    }, [shipment, items, __]);
    let onChangeOrder = React.useCallback((e: { target: { name: string, value: string } } | string) => {
        setShipment(old => {
            if (typeof e === 'string') {
                return { ...old, status: e };
            } else {
                return { ...old, [e.target.name]: e.target.value };
            }
        });
    }, [setShipment]);
    let onChangeItem = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setItems(old => {
            const { 0: key, 1: index } = e.target.name.substring(0, e.target.name.length - 1).split('[');
            let cloned = [...old];
            //@ts-ignore
            cloned[parseInt(index)][key] = e.target.value;
            return cloned;
        });
    }, [setItems]);
    let add = React.useCallback((e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setItems(old => ([...old, { id: '', sku: '', qty: 0, shipment_id: shipment.id }]));
    }, [setItems, shipment]);
    React.useEffect(() => {
        let id = query.get('id');
        if (id) {
            let cached = window.sessionStorage.getItem('SS-' + id);
            if (cached) {
                let shipment = JSON.parse(cached);
                setShipment(shipment);
                setCrumbs([{ label: 'Shipment', link: '/sales-shipment' }, shipment.shipment_id]);
            } else {
                getSalesShipment({ filter: { id }, limit: 1 }).then(response => {
                    if (typeof response !== 'string' && response.data.length) {
                        setCrumbs([{ label: 'Shipment', link: '/sales-shipment' }, response.data[0].shipment_id]);
                        setShipment(response.data[0]);
                    }
                });
            }
            getSalesShipmentItem({ filter: { shipment_id: id }, limit: -1 }).then(response => {
                if (typeof response !== 'string') {
                    setItems(response.data);
                }
            });
        } else if (id = query.get('order_id')) {
            let cached = window.sessionStorage.getItem('SO-' + id);
            if (cached) {
                let order = JSON.parse(cached) as SalesOrder;
                setShipment({ id: '', order_id: order.order_id, shipment_id: order.order_id.includes('SO') ? order.order_id.replace('SO', 'SS') : '', customer_id: '', customer_name: '', status: 'Placed' });
            } else {
                getSalesOrder({ filter: { id }, limit: 1 }).then(response => {
                    if (typeof response !== 'string' && response.data.length) {
                        setShipment({ id: '', order_id: response.data[0].order_id, shipment_id: response.data[0].order_id.includes('SO') ? response.data[0].order_id.replace('SO', 'SS') : '', customer_id: '', customer_name: '', status: 'Placed' });
                    }
                });
            }
            getSalesOrderItem({ filter: { order_id: id }, limit: -1 }).then(response => {
                if (typeof response !== 'string') {
                    let items: Array<SalesShipmentItem> = [];
                    for (let item of response.data) {
                        items.push({ id: '', sku: item.sku, qty: item.qty, shipment_id: shipment.id });
                    }
                    setItems(items);
                }
            });
            setCrumbs([{ label: 'Shipment', link: '/sales-shipment' }, 'Edit']);
        } else {
            setCrumbs([{ label: 'Shipment', link: '/sales-shipment' }, 'Edit']);
        }
        let statuses = window.sessionStorage.getItem('SS-status');
        if (statuses) {
            setStatuses(JSON.parse(statuses));
        } else {
            getSalesOrderStatuses().then(response => {
                let result = response.map(item => ({ value: item.status, label: item.status + '(' + item.count + ')' }));
                window.sessionStorage.setItem('SS-status', JSON.stringify(result));
                setStatuses(result);
            });
        }
    }, [query, setShipment, setItems, props.editable]);
    return (
        <form className="edit h-100" onSubmit={save}>
            <div className="accordion">
                <MDBCard className="mb-3">
                    <MDBCardBody>
                        <input type="hidden" name="id" value={shipment.id} />
                        <MDBInput label={__('Shipment ID')} required readOnly={!props.editable} wrapperClass="mb-3" className="active" name="shipment_id" value={shipment.shipment_id} onChange={onChangeOrder} />
                        <MDBInput label={__('Order ID')} required readOnly={!props.editable} wrapperClass="mb-3" className="active" name="order_id" value={shipment.order_id} onChange={onChangeOrder} />
                        <MDBInput label={__('Customer ID')} required readOnly={!props.editable} wrapperClass="mb-3" className="active" name="customer_id" value={shipment.customer_id} onChange={onChangeOrder} />
                        <MDBInput label={__('Customer Name')} required readOnly={!props.editable} wrapperClass="mb-3" className="active" name="customer_name" value={shipment.customer_name} onChange={onChangeOrder} />
                        <Autocomplete label={__('Status')} required readOnly={!props.editable} name="status" value={shipment.status} options={statuses?.length ? statuses : DEFAULT_STATUSES} onChange={onChangeOrder} />
                    </MDBCardBody>
                </MDBCard>
                <MDBCard>
                    <MDBCardBody>
                        <MDBTable>
                            <MDBTableHead>
                                <tr>
                                    <th>SKU</th>
                                    <th className="text-nowrap text-center">{__('Qty')}</th>
                                </tr>
                            </MDBTableHead>
                            {props.editable ? (
                                <>
                                    <MDBTableBody>
                                        {items.map((item, i) => (
                                            <tr key={item.id + i.toString()}>
                                                <th>
                                                    <input type="hidden" name={'id[' + i + ']'} value={item.id} />
                                                    <MDBInput name={'sku[' + i + ']'} value={item.sku} onChange={onChangeItem} />
                                                </th>
                                                <td><MDBInput type="number" min="0" step="0.0001" name={'qty[' + i + ']'} value={item.qty} onChange={onChangeItem} /></td>
                                            </tr>
                                        ))}
                                    </MDBTableBody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan={6}>
                                                <MDBBtn color="light" block onClick={add}>
                                                    <FontAwesomeIcon icon={faPlusCircle} size="2x" />
                                                </MDBBtn>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </>
                            ) : (
                                <MDBTableBody>
                                    {items.map(item => (
                                        <tr key={item.id}>
                                            <th>{item.sku}</th>
                                            <td className="text-center">{parseFloat(item.qty.toString())}</td>
                                        </tr>
                                    ))}
                                </MDBTableBody>
                            )}
                        </MDBTable>
                    </MDBCardBody>
                </MDBCard>
            </div>
            {props.editable ? (
                <MDBCard className="tools">
                    <MDBCardBody>
                        <div className="btns">
                            <MDBBtn color="info" type="reset"><I18N>Reset</I18N></MDBBtn>
                            <MDBBtn color="primary" type="submit"><I18N>Save</I18N></MDBBtn>
                        </div>
                    </MDBCardBody>
                </MDBCard>
            ) : null}
        </form>
    );
}